import { useEffect, useState, useCallback } from "react";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { api } from "../lib/api";

const TEST_TYPES = ["BP", "ECG", "SpO2", "Stethoscope", "Temperature"];

export default function AdminDiagnostics() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [clinicId, setClinicId] = useState("");
  const [testType, setTestType] = useState("");
  const [abnormal, setAbnormal] = useState("any");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [clinics, setClinics] = useState([]);

  useEffect(() => { api.get("/admin/clinics?page_size=200").then((r) => setClinics(r.data.items)); }, []);

  const load = useCallback(async () => {
    const p = new URLSearchParams({ page: String(page), page_size: "30" });
    if (clinicId) p.set("clinic_id", clinicId);
    if (testType) p.set("test_type", testType);
    if (abnormal !== "any") p.set("abnormal", abnormal);
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    const { data } = await api.get(`/admin/diagnostic-tests?${p.toString()}`);
    setItems(data.items); setTotal(data.total);
  }, [clinicId, testType, abnormal, dateFrom, dateTo, page]);

  useEffect(() => { load(); }, [load]);

  const openReport = async (t) => {
    try {
      const res = await api.get(`/diagnostic-tests/${t.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      alert("Could not load report: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <AdminLayout title="Diagnostics" subtitle={`${total} test${total === 1 ? "" : "s"}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Select value={clinicId || "all"} onValueChange={(v) => { setClinicId(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="diag-clinic-filter" className="w-56 rounded-full"><SelectValue placeholder="Clinic" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All clinics</SelectItem>{clinics.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={testType || "all"} onValueChange={(v) => { setTestType(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="diag-type-filter" className="w-44 rounded-full"><SelectValue placeholder="Test type" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All types</SelectItem>{TEST_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={abnormal} onValueChange={(v) => { setAbnormal(v); setPage(1); }}>
          <SelectTrigger data-testid="diag-abnormal-filter" className="w-40 rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any flag</SelectItem>
            <SelectItem value="normal">Normal only</SelectItem>
            <SelectItem value="abnormal">Abnormal only</SelectItem>
          </SelectContent>
        </Select>
        <Input data-testid="diag-from" type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="w-44 rounded-full" />
        <Input data-testid="diag-to" type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="w-44 rounded-full" />
      </div>

      <div data-testid="admin-diagnostics-table" className="bg-white border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]">
            <thead className="bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="text-left p-3">Test</th><th className="text-left p-3">Clinic</th>
                <th className="text-left p-3">Patient</th><th className="text-left p-3">Doctor</th>
                <th className="text-left p-3">Type</th><th className="text-left p-3">Summary</th>
                <th className="text-left p-3">Status</th><th className="text-left p-3">Flag</th>
                <th className="text-left p-3">Started</th><th className="text-right p-3">Report</th></tr>
            </thead>
            <tbody>
              {items.length === 0 ? <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">No tests</td></tr>
                : items.map((t) => (
                <tr key={t.id} data-testid={`diag-row-${t.id}`} className="border-t border-border hover:bg-secondary/30">
                  <td className="p-3 font-mono text-xs">{t.id}</td>
                  <td className="p-3 text-xs">{t.clinic_name || "—"}</td>
                  <td className="p-3 text-xs">{t.patient_name || "—"}</td>
                  <td className="p-3 text-xs">{t.doctor_name || "—"}</td>
                  <td className="p-3 font-mono text-xs">{t.test_type}</td>
                  <td className="p-3 text-xs max-w-[260px] truncate">{t.summary?.headline || t.summary || "—"}</td>
                  <td className="p-3 text-xs">{t.status}</td>
                  <td className="p-3 text-xs">
                    {t.abnormal_flag === true ? <span className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 font-semibold">Abnormal</span>
                      : t.abnormal_flag === false ? <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Normal</span>
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">{t.started_at ? new Date(t.started_at).toLocaleString("en-IN") : "—"}</td>
                  <td className="p-3 text-right"><Button size="sm" variant="ghost" data-testid={`diag-view-report-${t.id}`} onClick={() => openReport(t)} disabled={t.status !== "completed"}>View report</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {total > 30 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Page {page} of {Math.ceil(total / 30)}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <Button size="sm" variant="outline" disabled={page >= Math.ceil(total / 30)} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
